import {
  EllipsisIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { MyFormInput } from "@/components/zthd/MyFormField";
import { ListLoading } from "@/components/list/status-text";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import {
  mutateRequest,
  postFetcher,
  putFetcherKeyed,
  delFetcher,
} from "@/lib/swr";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import ReportDocDialog, {
  type ReportDocFormData,
} from "@/components/reports/report-doc-dialog";
import type { Version } from "@/types/domain";

export const VersionRowActions = ({ version }: { version: Version }) => {
  return (
    <div className="flex space-x-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <EllipsisIcon className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <EditVersion version={version} />
          <SubmitReportDoc version={version} />
          <AuditToggle version={version} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// The audit state lives in an external audit service; use the /versions/audits list to determine whether the current version is already in audit,
// a single button toggles Enable / Disable based on the state.
const AuditToggle = ({ version }: { version: Version }) => {
  const { toast } = useToast();
  const { data: audits } = useSWR<Version[]>(["/versions/audits"]);
  const isAudit =
    !!version.versionCode &&
    Array.isArray(audits) &&
    audits.some((a: Version) => a.versionCode === version.versionCode);

  const { trigger: enable, isMutating: enabling } = useSWRMutation(
    `/versions/${version.id}/audits`,
    postFetcher,
    {
      onSuccess: () => {
        mutateRequest(`/versions/audits`);
        toast({
          title: "Audit mode enabled",
          description: "Version has been set to audit mode successfully.",
        });
      },
      onError: (error) =>
        toastError(
          toast,
          "Failed to enable audit mode",
          error,
          "An error occurred while enabling audit mode.",
        ),
    },
  );

  const { trigger: disable, isMutating: disabling } = useSWRMutation(
    `/versions/${version.id}/audits`,
    delFetcher,
    {
      onSuccess: () => {
        mutateRequest(`/versions/audits`);
        toast({
          title: "Audit mode disabled",
          description: "Version has been removed from audit mode successfully.",
        });
      },
      onError: (error) =>
        toastError(
          toast,
          "Failed to disable audit mode",
          error,
          "An error occurred while disabling audit mode.",
        ),
    },
  );

  const isMutating = enabling || disabling;

  const handleConfirm = () => {
    if (isMutating) return;
    // isAudit already implies a truthy versionCode; the guard narrows the type.
    if (isAudit && version.versionCode) disable(version.versionCode);
    else enable();
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          {isAudit ? (
            <TriangleAlertIcon className="mr-2 h-4 w-4" />
          ) : (
            <PlusIcon className="mr-2 h-4 w-4" />
          )}
          {isAudit ? "Disable audit mode" : "Enable audit mode"}
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isAudit ? "Disable audit mode" : "Enable audit mode"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isAudit
              ? "Are you sure you want to disable audit mode for this version?"
              : "Are you sure you want to enable audit mode for this version?"}
            <br />
            <br />
            <strong>Version:</strong> {version.docName}
            <br />
            <strong>Version Code:</strong> {version.versionCode || "N/A"}
            <br />
            <br />
            {isAudit
              ? "This will remove the version from audit review process."
              : "This will put the version into audit review process."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isMutating}
            className={
              isAudit
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : undefined
            }
          >
            {isMutating ? (
              <>
                <RefreshCwIcon className="mr-2 h-4 w-4 animate-spin" />
                {isAudit ? "Disabling…" : "Enabling…"}
              </>
            ) : isAudit ? (
              "Confirm disable"
            ) : (
              "Confirm enable"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const editSchema = z.object({
  versionName: z.string().min(1, "version name is required"),
  versionCode: z.string().min(1, "version code is required"),
});

// Rename entry (version name / code).
const EditVersion = ({ version }: { version: Version }) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const currentValues = {
    versionName: version.versionName ?? "",
    versionCode: version.versionCode ?? "",
  };
  const form = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: currentValues,
  });

  const { trigger, isMutating, error } = useSWRMutation(
    `/versions/${version.id}#edit`,
    putFetcherKeyed,
    {
      onSuccess: () => {
        setOpen(false);
        mutateRequest("/versions");
        mutateRequest(`/versions/${version.id}`);
        toast({
          title: "Version updated",
          description: "Version name/code has been updated.",
        });
      },
      onError: (err) =>
        toastError(
          toast,
          "Update failed",
          err,
          "An error occurred while updating the version.",
        ),
    },
  );

  const onOpenChange = (o: boolean) => {
    if (o) form.reset(currentValues);
    setOpen(o);
  };

  const handleSubmit = (data: z.infer<typeof editSchema>) => {
    if (isMutating) return;
    trigger(data);
  };

  return (
    <>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
      >
        <PencilIcon className="mr-2 h-4 w-4" />
        Edit version
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit version</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              <div className="space-y-2">
                <MyFormInput
                  name="versionName"
                  form={form}
                  label="Version name"
                  placeholder="major.version.code8.app (eg: 3.5.38.1-fix)"
                />
              </div>
              <div className="space-y-2">
                <MyFormInput
                  name="versionCode"
                  form={form}
                  label="Version code"
                  placeholder="eg:1300530"
                />
              </div>
              {isMutating ? (
                <ListLoading>Saving…</ListLoading>
              ) : (
                <Button
                  type="submit"
                  disabled={
                    !form.formState.isValid || form.formState.isSubmitting
                  }
                >
                  Save
                </Button>
              )}
              {error && <div className="text-destructive">{error.message}</div>}
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Report submission from the row menu — same shared dialog / same
// PUT { reportDoc } semantics as everywhere else (the legacy single
// `reportDocUrl` field is gone).
const SubmitReportDoc = ({ version }: { version: Version }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const { trigger, isMutating } = useSWRMutation(
    `/versions/${version.id}#report-doc`,
    putFetcherKeyed,
    {
      onSuccess: () => {
        setIsOpen(false);
        setError(null);
        mutateRequest(`/versions`);
        mutateRequest(`/versions/${version.id}`);
        toast({
          title: "Submit success",
          description: `The report document has been submitted successfully.`,
        });
      },
      onError: (err) => {
        setError(
          err?.message ||
            "An error occurred while submitting the report document.",
        );
        toastError(
          toast,
          "Submit failed",
          err,
          "An error occurred while submitting the report document.",
        );
      },
    },
  );

  const onSubmit = async (data: ReportDocFormData) => {
    await trigger({
      reportDoc: {
        url: data.url,
        name: "",
        result: data.result,
        summary: data.summary,
      },
    });
  };

  return (
    <>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}
      >
        <RefreshCwIcon className="mr-2 h-4 w-4" />
        Submit report document
      </DropdownMenuItem>
      <ReportDocDialog
        open={isOpen}
        onOpenChange={(o) => {
          setIsOpen(o);
          if (!o) setError(null);
        }}
        onSubmit={onSubmit}
        version={version}
        isSubmitting={isMutating}
        error={error}
      />
    </>
  );
};

export default VersionRowActions;
